'use client';

import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import { useT } from '@/components/i18n-provider';
import { LayoutGrid, List } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ViewMode = 'cards' | 'rows';

/**
 * Responsive card grid that reflows with the (resizable) panel width: each
 * column is at least ~150px, so the grid is 1-up when the panel is narrow and
 * fills to more columns as it widens. Use together: `className={cardGridClass}
 * style={cardGridStyle}`.
 */
export const cardGridClass = 'grid gap-2';
export const cardGridStyle: CSSProperties = {
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
};

/**
 * Per-surface card/row preference, persisted in localStorage. Default is
 * `cards` (the app-store look); users can flip any tab to dense `rows`.
 * `key` namespaces the preference, e.g. 'apps:explore', 'agents:yours'.
 */
export function useViewMode(key: string): [ViewMode, (m: ViewMode) => void] {
  const storageKey = `mkt:viewmode:${key}`;
  const [mode, setMode] = useState<ViewMode>('cards');
  useEffect(() => {
    try {
      const v = localStorage.getItem(storageKey);
      if (v === 'cards' || v === 'rows') setMode(v);
    } catch {
      /* ignore */
    }
  }, [storageKey]);
  const set = useCallback(
    (m: ViewMode) => {
      setMode(m);
      try {
        localStorage.setItem(storageKey, m);
      } catch {
        /* ignore */
      }
    },
    [storageKey],
  );
  return [mode, set];
}

/** Live (listed) vs Unlisted badge for an owned listing. Null if not published.
 * Carries the owner's install count UNFLOORED — this is the maker-loop
 * read-half: the number is written on every attributed spawn, and the owner
 * seeing "3 installs" is signal, not emptiness (public cards floor it). */
export function ListingBadge({ listing, className }: { listing?: { listed?: boolean; installCount?: number | null } | null; className?: string }) {
  const t = useT();
  if (!listing) return null;
  const listed = listing.listed !== false;
  const installs = typeof listing.installCount === 'number' ? listing.installCount : null;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 text-[10px] font-medium',
        listed ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400',
        className,
      )}
    >
      <span className={cn('inline-block h-1.5 w-1.5 rounded-full', listed ? 'bg-emerald-500' : 'bg-amber-500')} />
      {listed ? t('workspace.panels.live') : t('workspace.panels.unlisted')}
      {installs !== null && installs > 0 && (
        <span className="font-normal text-muted-foreground">
          · {installs} {installs === 1 ? 'install' : 'installs'}
        </span>
      )}
    </span>
  );
}

export function ViewModeToggle({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  const t = useT();
  return (
    <div className="inline-flex shrink-0 items-center gap-0.5 rounded-md border border-border/60 p-0.5">
      <button
        type="button"
        aria-label={t('workspace.panels.cardView')}
        aria-pressed={mode === 'cards'}
        onClick={() => onChange('cards')}
        className={cn(
          'rounded p-1 transition-colors',
          mode === 'cards' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <LayoutGrid className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label={t('workspace.panels.rowView')}
        aria-pressed={mode === 'rows'}
        onClick={() => onChange('rows')}
        className={cn(
          'rounded p-1 transition-colors',
          mode === 'rows' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <List className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
